import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ListBook, Modal, tokens } from '@arvoreeducacao/bonsai-ds'
import BookIndication from '../book-actions/BookIndication'
import {
  ListItemBox,
  ListItemDisabled,
  ListItemPointer,
  SheetContainer
} from './BookModal.styles'
import type { BookModalProps } from './BookModal.types'
import ButtonToggleFavorite from 'components/modular/button-actions/button-toggle-favorite'
import ButtonAddToList from 'components/modular/button-actions/button-add-to-list'
import ButtonDownloadOffline from 'components/modular/button-actions/button-download-offline'
import ButtonDownloadArticle from 'components/modular/button-actions/button-download-article/ButtonDownloadArticle'
import { redirectToIntermediatePage } from 'utils/identify-modules/IdentifyModules'
import { useHistory } from 'react-router-dom'
import { DOCUMENT_LANGUAGE_BY_APP_LANGUAGE, useTranslation } from 'i18n'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import { IndicationContentType } from 'api/data.types'
import useFeatureToggles from 'utils/hooks/use-feature-toggles'
import { useNewsLoan } from 'screens/site/intermediate/components/intermediate-button-actions/useNewsLoan'
import {
  isRenewalWindowOpen,
  renewalWindowOpeningDay
} from 'utils/loan-due-days'

const MAX_RENEWALS = 2

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'

const intermediatePathByContentType = (
  contentType: BookModalProps['contentType'],
  slug: string
) => {
  if (contentType === 'video') return `/app-v2/videos/${slug}`
  if (contentType === 'podcast') return `/app-v2/podcasts/${slug}`
  return null
}

const BookModal: React.FC<BookModalProps> = ({
  setIsOpen,
  title,
  author,
  imageUrlThumb,
  slug,
  additionalActions,
  contentType,
  id,
  loanActionInfo,
  mediaFavoriteInfo,
  isMyReadingsPath
}) => {
  const [showModal, setShowModal] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null)

  const history = useHistory()
  const { t, i18n } = useTranslation()
  const { user } = useUserInfo()
  const { featureToggles } = useFeatureToggles()
  const isBook = contentType !== 'video' && contentType !== 'podcast'
  const isArticle = contentType === 'article'
  const seedPrPocEnabled = featureToggles?.seedPrPoc === true
  const { hasActiveLoan: hasActiveNewsLoan } = useNewsLoan(
    id,
    isArticle && seedPrPocEnabled
  )
  const indicationContentType =
    contentType === 'article'
      ? IndicationContentType.ARTICLE
      : contentType === 'video'
      ? IndicationContentType.VIDEO
      : IndicationContentType.BOOK
  const canIndicate = isBook || (contentType === 'video' && seedPrPocEnabled)

  const loanStatus = loanActionInfo?.loanStatus
  const isReturnableLoan = loanStatus === 'pending' || loanStatus === 'active'
  const renewalsCount = loanActionInfo?.renewalsCount ?? 0
  const canReturn = isReturnableLoan && Boolean(loanActionInfo?.onReturn)
  const renewalLimitReached = renewalsCount >= MAX_RENEWALS
  const renewalWindowOpen = isRenewalWindowOpen(loanActionInfo?.dueAt)
  const showRenew = isReturnableLoan && Boolean(loanActionInfo?.onRenew)
  const canRenew = showRenew && !renewalLimitReached && renewalWindowOpen
  const renewalOpeningDay = renewalWindowOpeningDay(
    loanActionInfo?.dueAt,
    DOCUMENT_LANGUAGE_BY_APP_LANGUAGE[i18n.language]
  )
  const renewalWindowReason = renewalOpeningDay
    ? t('Você poderá renovar a partir de {{date}}', {
        date: renewalOpeningDay
      })
    : t('A renovação fica disponível quando faltar 1 dia para expirar')
  const renewBlockedReason = renewalLimitReached
    ? t('Você atingiu o limite de renovações deste empréstimo')
    : renewalWindowReason

  useEffect(() => {
    const trigger = document.activeElement as HTMLElement | null

    const currentDialog = () => {
      const visible = Array.from(
        document.querySelectorAll<HTMLElement>('[role="dialog"]')
      ).filter(el => el.offsetWidth > 0 || el.offsetHeight > 0)
      const candidates = visible.length
        ? visible
        : Array.from(document.querySelectorAll<HTMLElement>('[role="dialog"]'))

      return candidates[candidates.length - 1] ?? null
    }

    const focusablesOf = (dialog: HTMLElement) => {
      const all = Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      )
      const rendered = all.filter(
        el => el.offsetWidth > 0 || el.offsetHeight > 0
      )

      return rendered.length ? rendered : all
    }

    const focusFirstOption = () => {
      const dialog = currentDialog()

      if (!dialog) {
        return false
      }

      dialog.setAttribute('aria-modal', 'true')

      const [firstFocusable] = focusablesOf(dialog)
      if (firstFocusable) {
        firstFocusable.focus()
      } else {
        dialog.setAttribute('tabindex', '-1')
        dialog.focus()
      }

      return dialog.contains(document.activeElement)
    }

    const openFrame = focusFirstOption()
      ? null
      : requestAnimationFrame(focusFirstOption)

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
        return
      }

      const dialog = currentDialog()

      if (event.key !== 'Tab' || !dialog) return

      const focusables = focusablesOf(dialog)

      if (!focusables.length) {
        event.preventDefault()
        return
      }

      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement as HTMLElement | null

      if (!active || !dialog.contains(active)) {
        event.preventDefault()
        first.focus()
        return
      }

      if (event.shiftKey && active === first) {
        event.preventDefault()
        last.focus()
        return
      }

      if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      if (openFrame !== null) {
        cancelAnimationFrame(openFrame)
      }
      document.removeEventListener('keydown', handleKeyDown)
      trigger?.focus?.()
    }
  }, [setIsOpen])

  const handleViewDetails = () => {
    const mediaPath = intermediatePathByContentType(contentType, slug)
    if (mediaPath) {
      window.location.href = mediaPath
      return
    }
    redirectToIntermediatePage(
      history,
      contentType === 'article' ? 'article' : 'book',
      slug || id
    )
  }

  const handleReturn = () => {
    loanActionInfo?.onReturn?.()
    setIsOpen(false)
  }

  const handleRenew = () => {
    loanActionInfo?.onRenew?.()
    setIsOpen(false)
  }

  return createPortal(
    <SheetContainer ref={containerRef} onClick={e => e.stopPropagation()}>
      <Modal
        isOpen={showModal}
        type="standard"
        size="small"
        title={t('Mais opções')}
        titleSize={4}
        hasDesktopCloseButton
        onClose={() => setIsOpen(false)}
        center
        sheetHeaderProps={{
          type: 'close',
          title: t('Mais opções')
        }}
      >
        <ListItemBox>
          <ListBook
            title={title}
            subtitle={author}
            coverThumb={imageUrlThumb}
            hideButton
            contentFormat={contentType === 'article' ? 'newscover' : 'book'}
          />
          <ListItemPointer
            icon="info"
            text={t('Ver detalhes')}
            onClick={() => {
              setShowModal(false)
              handleViewDetails()
            }}
          />
          {mediaFavoriteInfo && (
            <ListItemPointer
              icon={mediaFavoriteInfo.isFavorite ? 'heart-fill' : 'heart'}
              text={
                mediaFavoriteInfo.isFavorite
                  ? t('Remover dos favoritos')
                  : t('Favoritar')
              }
              onClick={mediaFavoriteInfo.onToggle}
            />
          )}
          {!user?.isContextNonEducational &&
            !featureToggles?.biblionGeneral &&
            !isMyReadingsPath &&
            canIndicate && (
              <BookIndication
                onClose={() => {
                  setShowModal(true)
                  setIsOpen(false)
                }}
                slug={slug}
                contentType={indicationContentType}
                author={author}
                title={title}
                setIsOpen={setIsOpen}
                id={id}
                imageUrlThumb={imageUrlThumb}
              >
                <ListItemPointer icon="users" text={t('Indicar')} />
              </BookIndication>
            )}
          {isBook && !isMyReadingsPath && (
            <ButtonToggleFavorite slug={slug} noBorder hasText />
          )}
          {isBook && !isArticle && featureToggles?.preferenceList === true && (
            <ButtonAddToList slug={slug} onModalClose={() => setIsOpen(false)}>
              <ListItemPointer icon="list" text={t('Adicionar à lista')} />
            </ButtonAddToList>
          )}
          {isBook &&
            !isArticle &&
            !isMyReadingsPath &&
            !featureToggles?.biblionGeneral &&
            (!seedPrPocEnabled || isReturnableLoan) && (
              <ButtonDownloadOffline
                slug={slug}
                title={title}
                author={author}
                imageUrl={imageUrlThumb}
                noBorder
                hasText
                onDownloadStart={() => setIsOpen(false)}
              />
            )}
          {contentType === 'video' &&
            seedPrPocEnabled &&
            isReturnableLoan &&
            !featureToggles?.biblionGeneral && (
              <ButtonDownloadOffline
                slug={slug}
                title={title}
                author={author}
                imageUrl={imageUrlThumb}
                contentType="video"
                noBorder
                hasText
                onDownloadStart={() => setIsOpen(false)}
              />
            )}
          {isArticle && id && (!seedPrPocEnabled || hasActiveNewsLoan) && (
            <ButtonDownloadArticle
              articleId={id}
              seedPrPocEnabled={seedPrPocEnabled}
              noBorder
              hasText
            />
          )}
          {canReturn && (
            <ListItemPointer
              icon="back"
              text={t('Devolver')}
              onClick={handleReturn}
            />
          )}
          {showRenew &&
            (canRenew ? (
              <ListItemPointer
                icon="sync"
                text={t('Renovar')}
                onClick={handleRenew}
              />
            ) : (
              <ListItemDisabled
                icon="sync"
                text={t('Renovar')}
                subtitle={renewBlockedReason}
                color={tokens.colors.dark3}
                colorSubtitle={tokens.colors.dark5}
              />
            ))}
          {additionalActions}
        </ListItemBox>
      </Modal>
    </SheetContainer>,
    document.getElementById('modal')
  )
}

export default BookModal
